import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'

/**
 * Every rail block that can give way on a short window, in the order it does:
 * the first goes first. ONE order for every rail, so a block weighs the same
 * on every screen that draws it; a rail simply skips the ids it does not have.
 *
 *   reminders      Portfolio's Reminders — "not available yet", no endpoint
 *   highlights     the Broker rail's Today's highlights — likewise, no endpoint
 *   happening      Portfolio's What's happening — the same, no endpoint
 *   fear-greed     the market's reading (MarketRail, on /market) or the
 *                  company's (StockRail) — a single number, the least to lose
 *   range52        NEPSE's 52-week range (MarketRail, on /market) — one bar
 *   unlocks        Home's "Shares unlocking soon" — no data behind it yet
 *   prices         Home's "Prices today" — likewise, and a day-old quote
 *   stocks         Popular stocks — market-wide, and one click from Movers
 *   movers         Top movers (MarketRail, on /market) — likewise
 *   watchlist      Home's "Your watchlist" — the member's own names
 *   news           Market news
 *   company-news   "News about {symbol}", the Stock rail's reason to exist
 *   top-rated      the Broker rail's Top rated brokers, its one live block
 *   health         Portfolio health, the one live block on Portfolio's rail
 *
 * A block only goes once every list in the rail is already down to one row:
 * rows give way first, blocks second (see `computeRailFit`).
 */
export const RAIL_DROP_ORDER = [
  'reminders',
  'highlights',
  'happening',
  'fear-greed',
  'range52',
  'unlocks',
  'prices',
  'stocks',
  'movers',
  'watchlist',
  'news',
  'company-news',
  'top-rated',
  'health',
] as const

export type RailBlockId = (typeof RAIL_DROP_ORDER)[number]

const isRailBlockId = (value: string | undefined): value is RailBlockId =>
  RAIL_DROP_ORDER.some((id) => id === value)

/** A block as the fit prices it, in px. */
export interface RailBlockMeasure {
  id: RailBlockId
  /**
   * What the block costs beyond its rows: its own height with every row
   * taken out, plus the gap it holds open beside the next block. Dropping
   * the block saves this and its shown rows.
   */
  cost: number
  /** Each candidate row's height, shown and parked alike, in order. Empty
   *  for a block with no list (Fear and greed), or one still waiting on its
   *  data in an empty or failed state. */
  rows: readonly number[]
}

/** The rail, measured: what `computeRailFit` spends `availableHeight` on. */
export interface RailMeasure {
  /** Everything no block owns, in px. Negative by one gap when every child
   *  of the rail's column is a block, since each block's cost carries the
   *  gap after it and the last one has none. */
  base: number
  /** In the rail's own order, which is the order the fill hands out rows. */
  blocks: readonly RailBlockMeasure[]
}

/** How the rail draws: rows per block, and the blocks it leaves out. */
export interface RailFit {
  rows: Readonly<Partial<Record<RailBlockId, number>>>
  dropped: readonly RailBlockId[]
}

const sumOf = (heights: readonly number[], count: number): number =>
  heights.slice(0, count).reduce((sum, height) => sum + height, 0)

/**
 * The most of the rail that fits in `availableHeight`, priced on measured
 * heights rather than a fixed figure per row: a Market-news headline wraps to
 * one line in a wide rail and three in a narrow one, so no constant is right
 * at every width.
 *
 *  1. Every list starts at its floor, one row.
 *  2. If even that does not fit, whole blocks leave in `RAIL_DROP_ORDER`
 *     until it does — or until nothing droppable is left, which no window of
 *     640px or more reaches.
 *  3. Rows are then handed out one at a time, a row to each list in turn in
 *     the rail's own order, while the next row fits. A list whose next row
 *     does not fit stops there and the others carry on, so a short row
 *     (Popular stocks, ≈45px) can still use room a tall one (a three-line
 *     headline) could not. A list also stops at its cap: the rows a card
 *     hands in are already sliced to the design's count.
 *
 * Pure, and a function of the measurement alone: the measurement is the same
 * whatever this returns (see `measureRail`), which is what stops the loop
 * between the two from oscillating.
 */
export function computeRailFit({ base, blocks }: RailMeasure, availableHeight: number): RailFit {
  const counts = new Map(blocks.map((block) => [block.id, Math.min(1, block.rows.length)]))
  const kept = new Set(blocks.map((block) => block.id))
  const countOf = (id: RailBlockId): number => counts.get(id) ?? 0

  const height = (): number =>
    blocks.reduce(
      (sum, block) =>
        kept.has(block.id) ? sum + block.cost + sumOf(block.rows, countOf(block.id)) : sum,
      base,
    )

  for (const id of RAIL_DROP_ORDER) {
    if (height() <= availableHeight) break
    kept.delete(id)
  }

  let total = height()
  let filling = blocks.filter((block) => kept.has(block.id))
  while (filling.length > 0) {
    filling = filling.filter((block) => {
      const next = block.rows[countOf(block.id)]
      if (next === undefined || total + next > availableHeight) return false
      counts.set(block.id, countOf(block.id) + 1)
      total += next
      return true
    })
  }

  const rows: Partial<Record<RailBlockId, number>> = {}
  for (const id of kept) rows[id] = countOf(id)
  return {
    rows,
    dropped: RAIL_DROP_ORDER.filter((id) => counts.has(id) && !kept.has(id)),
  }
}

/** A row-list's root inside a block — see `RailRows`. */
const RAIL_LIST_SELECTOR = '[data-rail-list]'
/** The rows a list draws, and the ones it parks out of sight to keep them measurable. */
const SHOWN_ROWS_SELECTOR = ':scope > [data-rail-rows] > *'
const ROWS_SELECTOR = `${SHOWN_ROWS_SELECTOR}, :scope > [data-rail-parked] > :not([data-rail-sentinel])`

const isShown = (element: Element): boolean => element.getClientRects().length > 0
const heightOf = (element: Element): number => element.getBoundingClientRect().height
/** Two decimals: enough to price a row, few enough that sub-pixel noise from
 *  one layout to the next does not read as a change. */
const round = (px: number): number => Math.round(px * 100) / 100
/** `Number`, not `parseFloat`: the repo keeps `parseFloat` for money. */
const pxOf = (value: string): number => Number(value.replace(/px$/, '')) || 0

/**
 * The live list in a block. A card mid-load can hold two for a moment — the
 * skeleton's, fading out over the rows that replaced it — and the leaving one
 * sits inside an `inert` layer, so it is skipped.
 */
function liveList(block: HTMLElement): HTMLElement | undefined {
  return [...block.querySelectorAll<HTMLElement>(RAIL_LIST_SELECTOR)].find(
    (list) => list.parentElement?.closest('[inert]') == null,
  )
}

/**
 * Reads the rail's blocks off the DOM: every `[data-rail-block]` and the rows
 * of the list inside it.
 *
 * ── Why every row can be measured, shown or not ───────────────────────────
 *
 * `RailRows` never unmounts a row the fit leaves out: it parks it in an
 * invisible, inert, zero-height layer at the list's own width. A parked row
 * wraps exactly as it would if shown, so its height is known BEFORE the
 * window grows enough to bring it back — which is what lets the fit add a
 * row without first drawing one too many to find out what it costs.
 *
 * ── Why the answer does not move with the fit ─────────────────────────────
 *
 * A block's `cost` is its height minus its SHOWN rows, so showing a row more
 * or less changes both terms by the same amount; parked rows keep their
 * heights; `base` is what is left. So the fit this measurement produces does
 * not change the measurement, and the next pass bails out instead of
 * oscillating. (The last shown row drops its hairline, a pixel; `RailRows`
 * keeps a sentinel after the parked rows so none of them drops it too, which
 * keeps that error to one pixel on the safe side.)
 *
 * A block the fit has dropped is `hidden` and measures 0, so its last
 * measurement stands in from `cache` — the rail has to know what a block
 * costs to bring it back.
 *
 * `null` while the rail is not laid out: below `xl` the track is
 * `display: none`, and jsdom lays nothing out at all.
 */
export function measureRail(
  rail: HTMLElement,
  cache: Map<RailBlockId, RailBlockMeasure>,
): RailMeasure | null {
  if (!isShown(rail)) return null

  let base = heightOf(rail)
  const blocks: RailBlockMeasure[] = []

  for (const element of rail.querySelectorAll<HTMLElement>('[data-rail-block]')) {
    const id = element.dataset.railBlock
    if (!isRailBlockId(id)) continue

    if (!isShown(element)) {
      const last = cache.get(id)
      if (last !== undefined) blocks.push(last)
      continue
    }

    const list = liveList(element)
    const rows = list === undefined ? [] : [...list.querySelectorAll(ROWS_SELECTOR)].map(heightOf)
    const shownRows =
      list === undefined
        ? 0
        : [...list.querySelectorAll(SHOWN_ROWS_SELECTOR)].reduce(
            (sum, row) => sum + heightOf(row),
            0,
          )
    const gap = element.parentElement ? pxOf(getComputedStyle(element.parentElement).rowGap) : 0
    const height = heightOf(element)

    const measured: RailBlockMeasure = {
      id,
      cost: round(height - shownRows + gap),
      rows: rows.map(round),
    }
    cache.set(id, measured)
    blocks.push(measured)
    base -= height + gap
  }

  return { base: round(base), blocks }
}

const sameMeasure = (a: RailMeasure | null, b: RailMeasure | null): boolean =>
  JSON.stringify(a) === JSON.stringify(b)

/** What a rail's blocks read: the fit, and a way to ask for a fresh one. */
export interface RailFitState {
  /** `null` until measured, and wherever no `ScreenLayout` fits a rail:
   *  every block then draws whole, with every row it has. */
  fit: RailFit | null
  /** Re-measures now, inside the caller's own layout effect, so a list whose
   *  rows just changed is re-fitted before the browser paints them. */
  remeasure: () => void
}

const UNFITTED: RailFitState = { fit: null, remeasure: () => undefined }

/** Provided by `ScreenLayout` around every rail it draws. */
export const RailFitContext = createContext<RailFitState>(UNFITTED)

export const useRailFitContext = (): RailFitState => useContext(RailFitContext)

/**
 * Spread onto a droppable block's own root: names it to the fit, and hides it
 * (the `hidden` attribute, which preflight makes `display: none !important`
 * over any layout class) once the fit drops it. Hidden rather than unmounted,
 * so its query keeps its data and it comes back without a reload.
 */
export function useRailBlock(id: RailBlockId): { 'data-rail-block': RailBlockId; hidden?: true } {
  const { fit } = useRailFitContext()
  return fit?.dropped.includes(id) === true
    ? { 'data-rail-block': id, hidden: true }
    : { 'data-rail-block': id }
}

/**
 * Measures a rail and fits it to `availableHeight`: `ScreenLayout`'s, for
 * every rail it draws, shared or a screen's own. Hand the result to
 * `RailFitContext`.
 *
 * The measurement is state; the fit is derived from it in render, against
 * whatever `availableHeight` is now. `availableHeight` lands from
 * `useStickyRailOffset`'s own layout effect, so a fit stored in an effect here
 * would be a frame behind it on mount — the one frame anybody would see.
 *
 * Re-measured on three things: the rail's own size (data landing, a webfont,
 * a block appearing), the window's (a narrower rail re-wraps every headline)
 * and a list's rows changing (`RailRows` calls `remeasure` from its own
 * layout effect, before paint). The first two are rAF-coalesced, so a member
 * dragging a window edge re-renders at most once a frame, and counts only
 * change where a row's height is crossed: they step, they never flicker.
 */
export function useRailFit(
  railRef: RefObject<HTMLElement | null>,
  availableHeight: number | null,
  enabled: boolean,
): RailFitState {
  const [measure, setMeasure] = useState<RailMeasure | null>(null)
  const cacheRef = useRef(new Map<RailBlockId, RailBlockMeasure>())
  const frameRef = useRef<number | null>(null)

  const remeasure = useCallback(() => {
    const rail = railRef.current
    if (rail === null) return
    const next = measureRail(rail, cacheRef.current)
    setMeasure((previous) => (sameMeasure(previous, next) ? previous : next))
  }, [railRef])

  useLayoutEffect(() => {
    const rail = railRef.current
    if (!enabled || rail === null || typeof ResizeObserver === 'undefined') return

    const schedule = () => {
      if (frameRef.current !== null) return
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null
        remeasure()
      })
    }

    // Synchronous first read, so the fit is in place before the first paint.
    remeasure()

    const ro = new ResizeObserver(schedule)
    ro.observe(rail)
    window.addEventListener('resize', schedule)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', schedule)
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [railRef, enabled, remeasure])

  const fit = useMemo(
    () =>
      !enabled || measure === null || availableHeight === null
        ? null
        : computeRailFit(measure, availableHeight),
    [enabled, measure, availableHeight],
  )

  return useMemo(() => ({ fit, remeasure }), [fit, remeasure])
}
